import { type ChangeEvent, type FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AutomationKindType } from "@/types/graphql/partner.generated";
import {
  broadcastRates,
  formatPercent,
  formatUsd,
  useAutomationFlowsListQuery,
} from "@/partner/entities/marketing";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { automationBulkSelection } from "../model/automationBulkSelection";
import { formatCount } from "../model/automationStats";
import {
  AUTOMATION_FILTER_STATUSES,
  AUTOMATION_LIST_KINDS,
  KIND_KEYS,
  labelKeyFor,
  listTrigger,
  triggerKey,
} from "../model/automationLabels";
import { JOURNEY_FALLBACK_KEY } from "../model/journeyLabels";
import { AutomationStatusChip } from "./AutomationStatusChip";
import { AutomationsListToolbar } from "./AutomationsListToolbar";
import { OverflowTooltip } from "./OverflowTooltip";

interface AutomationsListProps {
  onEdit: (id: string) => void;
  /** Кнопка в пустом состоянии без фильтра; необязательна для тестов. */
  onCreate?: () => void;
}

/**
 * Список автоматизаций и цепочек: Card + MUI Table, как список рассылок.
 * Вид из фильтра страницы (?kind=), колонки «Вид» и «Цель». На мобиле
 * только название и статус; длинное название не растягивает строку, а
 * обрезается многоточием с полным текстом в подсказке.
 */
export const AutomationsList: FC<AutomationsListProps> = ({
  onEdit,
  onCreate,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search, status, kind },
  } = useFilter();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const statusFilter = AUTOMATION_FILTER_STATUSES.find((s) => s === status);
  // Неизвестный вид в адресе отсекается списком видов страницы.
  const kindFilter = AUTOMATION_LIST_KINDS.find((k) => k === kind);
  const hasActiveFilter =
    Boolean(search) || Boolean(statusFilter) || Boolean(kindFilter);
  const { data, loading, error, refetch } = useAutomationFlowsListQuery({
    variables: {
      kinds: kindFilter ? [kindFilter] : AUTOMATION_LIST_KINDS,
      status: statusFilter,
      search: search || undefined,
      page,
      rowsPerPage,
    },
    fetchPolicy: "cache-and-network",
    // «Повторить» после ошибки показывает загрузку (финальное ревью, минор 3).
    notifyOnNetworkStatusChange: true,
  });

  const nodes = data?.automations.nodes ?? [];
  const count = data?.automations.count ?? 0;
  // Чистая функция (model/automationBulkSelection.ts): что уйдёт в архив
  // (всё выбранное) и что реально удалится (DELETE в allowedActions строки), см. её тест
  // и AutomationsListToolbar.tsx для остального массового поведения.
  const selection = automationBulkSelection(nodes, selected);
  const numSelected = selection.selectedIds.length;
  const allSelected = numSelected > 0 && numSelected === nodes.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;
  const onSelectAll = (event: ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked
        ? Object.fromEntries(nodes.map((item) => [item.id, true]))
        : {}
    );
  const percent = (value: number | null) =>
    value === null
      ? t("broadcasts.stats.noData")
      : formatPercent(value, i18n.language);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Alert severity="error" sx={{ width: "100%" }}>
            {t("automations.list.loadError")}
          </Alert>
          <Button
            variant="outlined"
            onClick={() => void refetch().catch(() => undefined)}
            disabled={loading}
          >
            {t("automations.list.retry")}
          </Button>
        </Stack>
      </Card>
    );
  }

  if (nodes.length === 0) {
    // Пустой результат фильтра (поиск/статус) не тот же текст, что пустой
    // список целиком: здесь автоматизации есть, просто не под этот фильтр,
    // предлагать создать первую было бы неверно.
    if (hasActiveFilter) {
      return <EmptyListCard title={t("automations.list.emptyFilter")} />;
    }
    return (
      <EmptyListCard
        title={t("automations.list.empty")}
        action={
          onCreate && (
            <Button variant="contained" disableElevation onClick={onCreate}>
              {t("common.actions.add")}
            </Button>
          )
        }
      />
    );
  }

  return (
    <Card elevation={0}>
      {/* Collapse, как в PARTNER-PAGES 2.3: таблица не прыгает на первом выборе. */}
      <Collapse in={numSelected > 0}>
        <AutomationsListToolbar
          indeterminate={indeterminate}
          allSelected={allSelected}
          numSelected={numSelected}
          onSelectAllClick={onSelectAll}
          selection={selection}
          onCloseSelected={() => setSelected({})}
        />
      </Collapse>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={allSelected}
                onChange={onSelectAll}
                slotProps={{
                  input: { "aria-label": t("automations.list.selectAll") },
                }}
              />
            </TableCell>
            <TableCell>{t("automations.list.name")}</TableCell>
            {!isMobile && <TableCell>{t("automations.filter.kind")}</TableCell>}
            {!isMobile && (
              <TableCell>{t("automations.list.trigger")}</TableCell>
            )}
            <TableCell>{t("automations.list.status")}</TableCell>
            {!isMobile && (
              <TableCell align="right">
                {t("automations.list.enrolled")}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("automations.stats.goalReached")}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">{t("automations.list.sent")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">{t("automations.list.opens")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("automations.list.revenue")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => {
            const trigger = listTrigger(item.triggerEvent);
            const rates = broadcastRates(item.stats);
            return (
              <TableRow
                key={item.id}
                hover
                selected={Boolean(selected[item.id])}
                onClick={() => onEdit(item.id)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell
                  padding="checkbox"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    color="default"
                    checked={Boolean(selected[item.id])}
                    slotProps={{
                      input: {
                        "aria-label": t("automations.list.selectRow", {
                          name: item.name,
                        }),
                      },
                    }}
                    onChange={(event) =>
                      setSelected({
                        ...selected,
                        [item.id]: event.target.checked,
                      })
                    }
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 0, width: "100%" }}>
                  {/* Tooltip клонирует ребёнка и вешает ref для позиционирования;
                      ItemTitleLink не forwardRef-компонент, поэтому оборачиваем
                      в span (нативный узел принимает ref без предупреждений). */}
                  <OverflowTooltip title={item.name}>
                    <Box component="span" sx={{ display: "block" }}>
                      <ItemTitleLink
                        to={`${PagePaths.automations}?tab=edit&id=${item.id}`}
                        onOpen={() => onEdit(item.id)}
                        color="text.primary"
                        underline="none"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        {item.name}
                      </ItemTitleLink>
                    </Box>
                  </OverflowTooltip>
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    {t(labelKeyFor(KIND_KEYS, item.kind, JOURNEY_FALLBACK_KEY))}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell>
                    {trigger ? t(triggerKey(trigger)) : null}
                  </TableCell>
                )}
                <TableCell>
                  <AutomationStatusChip status={item.status} />
                </TableCell>
                {!isMobile && (
                  <TableCell align="right">
                    {formatCount(item.stats.enrolled, i18n.language)}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">
                    {/* Цель есть только у цепочки: у автоматизации пусто. */}
                    {item.kind === AutomationKindType.JourneyType
                      ? formatCount(item.stats.goalReached, i18n.language)
                      : null}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">
                    {formatCount(item.stats.sent, i18n.language)}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">{percent(rates.open)}</TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">
                    {/* Итог в списке: первые покупки и продления подписок. */}
                    {formatUsd(
                      item.stats.revenueWithRenewalsUSD,
                      i18n.language
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
